import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tabs, Tab, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert
} from '@mui/material';
import Layout from '../components/Layout';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PeopleIcon from '@mui/icons-material/People';
import PendingIcon from '@mui/icons-material/Pending';
import FolderIcon from '@mui/icons-material/Folder';
import ReceiptIcon from '@mui/icons-material/Receipt';
import api from '../services/api';

export default function Admin() {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [stats, setStats] = useState({ users: [], projects: [], invoices: [] });
  const [search, setSearch] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, pendingRes, statsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/users/pending'),
        api.get('/admin/stats'),
      ]);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch admin data');
    }
  };

  const handleAction = async () => {
    const { action, user } = confirmDialog;
    try {
      await api.put(`/admin/users/${user.id}/${action}`);
      setAlert({ show: true, message: `User ${action}d successfully!`, severity: 'success' });
      fetchData();
    } catch (err) {
      setAlert({ show: true, message: `Failed to ${action} user`, severity: 'error' });
    }
    setConfirmDialog({ open: false, action: null, user: null });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 3000);
  };

  const totalUsers = stats.users.reduce((sum, u) => sum + Number(u.total), 0);
  const pendingCount = stats.users.filter(u => u.status === 'pending').reduce((sum, u) => sum + Number(u.total), 0);
  const totalProjects = stats.projects.reduce((sum, p) => sum + Number(p.total), 0);
  const totalInvoiceAmount = stats.invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + Number(i.amount || 0), 0);

  const statusConfig = {
    pending: { color: '#fff3e0', text: '#e65100' },
    approved: { color: '#e8f5e9', text: '#2e7d32' },
    rejected: { color: '#ffebee', text: '#c62828' },
    suspended: { color: '#f5f5f5', text: '#757575' },
  };

  const roleConfig = {
    homeowner: { color: '#e3f2fd', text: '#1565c0' },
    subcontractor: { color: '#f3e5f5', text: '#6a1b9a' },
    general_contractor: { color: '#e8f5e9', text: '#2e7d32' },
    admin: { color: '#fce4ec', text: '#880e4f' },
  };

  const filteredUsers = users.filter(u =>
    `${u.first_name} ${u.last_name} ${u.email} ${u.role}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout>
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 3, borderRadius: 2 }}>{alert.message}</Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>Admin Dashboard</Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>Manage users, approvals and platform overview</Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 32 }} />
              </Box>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>{totalUsers}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Total Users</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, background: 'linear-gradient(135deg, #e65100 0%, #ff6f00 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <PendingIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 32 }} />
              </Box>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>{pendingCount}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Pending Approvals</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <FolderIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 32 }} />
              </Box>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>{totalProjects}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Total Projects</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, background: 'linear-gradient(135deg, #6a1b9a 0%, #7b1fa2 100%)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <ReceiptIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 32 }} />
              </Box>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>${totalInvoiceAmount.toLocaleString()}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Total Paid Out</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3 }}>
        <Box sx={{ borderBottom: '1px solid #e8eaf6' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2 }}>
            <Tab label={`Pending Approvals (${pendingUsers.length})`} sx={{ fontWeight: 600 }} />
            <Tab label={`All Users (${users.length})`} sx={{ fontWeight: 600 }} />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          <TextField fullWidth placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)}
            size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment> }} />
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f6fa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Registered</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tab === 0 ? pendingUsers : filteredUsers).map(user => (
                <TableRow key={user.id} sx={{ '&:hover': { bgcolor: '#f5f6fa' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: '#1a237e', width: 36, height: 36, fontSize: 14 }}>
                        {user.first_name[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.first_name} {user.last_name}</Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>{user.email}</Typography>
                        {user.company_name && <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>🏢 {user.company_name}</Typography>}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={user.role.replace('_', ' ')} size="small"
                      sx={{ bgcolor: roleConfig[user.role]?.color, color: roleConfig[user.role]?.text, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={user.status} size="small"
                      sx={{ bgcolor: statusConfig[user.status]?.color, color: statusConfig[user.status]?.text, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#666' }}>{new Date(user.created_at).toLocaleDateString()}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {user.status !== 'approved' && (
                        <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}
                          onClick={() => setConfirmDialog({ open: true, action: 'approve', user })}
                          sx={{ bgcolor: '#2e7d32', borderRadius: 1, fontSize: 11, '&:hover': { bgcolor: '#1b5e20' } }}>
                          Approve
                        </Button>
                      )}
                      {user.status !== 'rejected' && (
                        <Button size="small" variant="contained" startIcon={<CancelIcon />}
                          onClick={() => setConfirmDialog({ open: true, action: 'reject', user })}
                          sx={{ bgcolor: '#c62828', borderRadius: 1, fontSize: 11, '&:hover': { bgcolor: '#b71c1c' } }}>
                          Reject
                        </Button>
                      )}
                      {user.status === 'approved' && (
                        <Button size="small" variant="outlined" startIcon={<PauseCircleIcon />}
                          onClick={() => setConfirmDialog({ open: true, action: 'suspend', user })}
                          sx={{ borderColor: '#e65100', color: '#e65100', borderRadius: 1, fontSize: 11 }}>
                          Suspend
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {(tab === 0 ? pendingUsers : filteredUsers).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                    <PeopleIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                    <Typography sx={{ color: '#999' }}>
                      {tab === 0 ? 'No pending approvals' : 'No users found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, user: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>
          Confirm {confirmDialog.action?.charAt(0).toUpperCase() + confirmDialog.action?.slice(1)}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to <strong>{confirmDialog.action}</strong> {confirmDialog.user?.first_name} {confirmDialog.user?.last_name}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, user: null })} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleAction}
            sx={{ bgcolor: confirmDialog.action === 'approve' ? '#2e7d32' : '#c62828', borderRadius: 2, px: 3 }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
